<template>
    <div class="comments">
        <div class="subtitle middle-sidebar-title is-5 has-text-weight-bold">
            <template v-if="parent">
                <span @click="open(null)" class="pointer">
                    <i class="icon fas fa-arrow-left"></i>
                    Go Back
                </span>

                <base-button
                    @click="resolve(!parent.resolved)"
                    icon="fas fa-check"
                    :class="{'is-primary': !parent.resolved}"
                    class="button is-small is-pulled-right"
                    :text="parent.resolved ? 'reopen' : 'resolve'"/> &nbsp;
            </template>

            <template v-else>
                <i @click="$emit('toggle_comments')"
                   class="icon fas fa-times close has-text-grey pointer"></i> &nbsp;

                <span>Threads &nbsp; </span>

                <base-button
                    @click="switch_resolved(false)"
                    :class="{'is-primary': !resolved}"
                    class="button is-small"
                    text="Active"/> &nbsp;

                <base-button
                    @click="switch_resolved(true)"
                    :class="{'is-primary': resolved}"
                    class="button is-small"
                    text="Resolved"/>
            </template>
        </div>

        <div class="comments-list scroll" ref="comments">
            <mugen-scroll
                :should-handle="!list.loading && !last_page"
                :handler="load"
                scroll-container="comments"
                ref="scroll">

                <p v-if="!last_page">loading...</p>
            </mugen-scroll>

            <div v-if="parent" class="parent-comment">
                {{parent.text}}
            </div>

           <comment-item
               v-for="comment in list.data.results"
               @click="open(comment)"
               :comment="comment"
               :parent="parent"
               :space="space"
               :note="note"
               :key="comment.id"/>
        </div>

        <div v-if="!resolved" class="text-input">
            <textarea
                v-model="text"
                @input="input"
                @keyup.enter="send"
                placeholder="Write a comment here..."
                ref="textarea"></textarea>

            <i v-if="!create.loading" @click="send" class="send-icon fas fa-paper-plane"></i>
            <div v-else class="loader send-loader"></div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import MugenScroll from 'vue-mugen-scroll';
import { copy } from '../../utils/helpers';
import CommentItem from './CommentItem.vue';

export default {
    name: 'CommentsList',
    components: {
        CommentItem, MugenScroll,
    },
    props: ['space', 'note'],
    data() {
        return {
            page: 1,
            text: '',
            parent: null,
            resolved: false,
            last_page: false,
        };
    },
    computed: {
        ...mapState('comments', ['list', 'create', 'update', 'remove']),
        ...mapState('user', ['user']),
    },
    methods: {
        load() {
            const scroll_height_before = this.$refs.comments.scrollHeight;
            const parent_id = this.parent ? this.parent.id : null;

            this.$store.dispatch('comments/list', {
                resolved: this.resolved,
                space_id: this.space.id,
                note_id: this.note.id,
                extend: this.page > 1,
                page: this.page,
                parent_id,
            }).then(() => {
                if (this.page === 1) {
                    // Scroll to bottom if loading first time
                    this.$refs.comments.scrollTo(0, this.$refs.comments.scrollHeight);
                } else {
                    // Scroll to position before load new comments
                    const scroll_height_after = this.$refs.comments.scrollHeight;
                    this.$refs.comments.scrollTo(0, scroll_height_after - scroll_height_before);
                }
                this.page += 1;
                this.last_page = !this.list.data.next;
            });
        },
        switch_resolved(resolved) {
            this.resolved = resolved;
            this.last_page = false;
            this.page = 1;
            this.load();
        },
        open(comment) {
            this.parent = copy(comment);
            this.last_page = false;
            this.resolved = false;
            this.page = 1;
            this.load();
        },
        resolve(resolved) {
            this.$store.dispatch('comments/update', {
                comment_id: this.parent.id,
                space_id: this.space.id,
                note_id: this.note.id,
                data: { resolved },
            }).then(() => {
                this.parent.resolved = resolved;
                this.$store.commit('notes/add_comments_count', resolved ? -1 : 1);
            });
        },
        input() {
            // Auto change height of text area
            this.$refs.textarea.style.minHeight = `${this.$refs.textarea.scrollHeight}px`;
        },
        send() {
            const parent = this.parent ? this.parent.id : null;

            this.$store.dispatch('comments/create', {
                space_id: this.space.id,
                note_id: this.note.id,
                data: { text: this.text, parent },
            }).then(() => {
                // Scroll to end
                this.$refs.comments.scrollTo(0, this.$refs.comments.scrollHeight);
                this.text = '';
                this.$store.commit('notes/add_comments_count', 1);
            });
        },
    },
};
</script>

<style scoped>
    .comments {
        width: 30rem;
    }

    .comments-list {
        height: calc(100vh - 11.5rem);
        border-radius: 0.3rem;
        padding-right: 0.15rem;
    }

    .comments-list::-webkit-scrollbar-track {
        background: transparent;
    }

    .text-input {
        position: relative;
        width: 100%;
        display: flex;
        background: #fff;
        border-radius: 0.3rem;
        border: solid 1px #d0d0d0;
        margin-top: 0.5rem;
    }

    .text-input textarea {
        width: 100%;
        padding: 0.8rem;
        height: 3rem;
        border: none;
        background: transparent;
        resize: none;
        font-size: 1.1em;
        flex-basis: 0;
        -webkit-box-flex: 1;
        -ms-flex-positive: 1;
        -ms-flex-negative: 1;
        flex-grow: 1;
        flex-shrink: 1;
        margin-top: 0.3rem;
    }

    .text-input textarea:focus {
        outline-width: 0;
    }

    .send-icon {
        padding: 0.8rem;
        font-size: 1.4em;
        color: #858585;
    }

    .send-loader {
        margin: 0.8rem;
    }

    .parent-comment {
        padding: 0.2rem;
        font-weight: bold;
        font-size: 0.95em;
    }
</style>
